// photo.ts — read a picked image file into a downscaled JPEG data-URL, ON-DEVICE only.
//
// Ported verbatim from Oriora Plant Diary's photo compressor. A hidden <input type=file> opens the
// native photo/camera sheet on iOS and the file picker on desktop; the decode + downscale run in a
// canvas on THIS device. The compressed data-URL is only sent onward when the user runs "Snap code",
// and then it goes as an ordinary billed brain call (brain.ts::extractCodeFromPhoto) — the same
// BYOK, customer-key path every other AI call in this app uses. Nothing is retained.
//
// We downscale to 1280px on the long edge and encode at JPEG q=0.85: small enough to send quickly,
// large enough that the text in a photographed code/error/diagram stays legible for OCR/vision. If
// the browser can't encode (rare), we fall back to the original data-URL rather than dropping it.

const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.85;

export function readDownscaledPhoto(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Could not read that photo — try another."));
    reader.onload = () => {
      const src = reader.result;
      if (typeof src !== "string") {
        reject(new Error("Could not read that photo — try another."));
        return;
      }
      const img = new Image();
      // A failed decode still yields a usable original — never lose the user's photo.
      img.onerror = () => resolve(src);
      img.onload = () => {
        const scale = Math.min(1, MAX_EDGE / Math.max(img.width, img.height));
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) { resolve(src); return; }
        ctx.drawImage(img, 0, 0, w, h);
        try { resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY)); }
        catch { resolve(src); }
      };
      img.src = src;
    };
    reader.readAsDataURL(file);
  });
}
